import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { AlertCircle, CheckCircle, Mail, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";

interface EmailBounce {
  id: string;
  user_id: string;
  email: string;
  bounce_type: 'hard' | 'soft' | 'complaint';
  bounce_reason: string | null;
  bounced_at: string;
  resolved_at: string | null;
}

export const EmailBounceManagement = () => {
  const queryClient = useQueryClient();

  const { data: bounces, isLoading } = useQuery({
    queryKey: ["email-bounces"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_bounces")
        .select("*")
        .order("bounced_at", { ascending: false });

      if (error) throw error;
      return data as EmailBounce[];
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async (bounceId: string) => {
      const { error } = await supabase
        .from("email_bounces")
        .update({ resolved_at: new Date().toISOString() })
        .eq("id", bounceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-bounces"] });
      toast.success("Bounce marked as resolved");
    },
    onError: (error) => {
      console.error("Error resolving bounce:", error);
      toast.error("Failed to resolve bounce");
    },
  });

  const getBounceIcon = (type: string) => {
    switch (type) {
      case 'hard':
        return <XCircle className="h-4 w-4 text-destructive" />;
      case 'complaint':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      case 'soft':
        return <Mail className="h-4 w-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getBounceVariant = (type: string): "destructive" | "default" | "secondary" => {
    switch (type) {
      case 'hard':
      case 'complaint':
        return 'destructive';
      case 'soft':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const activeBounces = bounces?.filter(b => !b.resolved_at) || [];
  const resolvedBounces = bounces?.filter(b => b.resolved_at) || [];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Active Bounces</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <span className="text-2xl font-bold">{activeBounces.length}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Hard Bounces</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" />
              <span className="text-2xl font-bold">
                {activeBounces.filter(b => b.bounce_type === 'hard').length}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Spam Complaints</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              <span className="text-2xl font-bold">
                {activeBounces.filter(b => b.bounce_type === 'complaint').length}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Email Bounces & Complaints</CardTitle>
          <CardDescription>
            Monitor and manage email delivery issues. Active bounces prevent email delivery.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading bounces...</div>
          ) : activeBounces.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
              <p>No active email bounces</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeBounces.map((bounce) => (
                  <TableRow key={bounce.id}>
                    <TableCell className="font-medium">{bounce.email}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getBounceIcon(bounce.bounce_type)}
                        <Badge variant={getBounceVariant(bounce.bounce_type)}>
                          {bounce.bounce_type}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {bounce.bounce_reason || 'No reason provided'}
                    </TableCell>
                    <TableCell>
                      {formatDistanceToNow(new Date(bounce.bounced_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell>
                      <Badge variant="destructive">Active</Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resolveMutation.mutate(bounce.id)}
                        disabled={resolveMutation.isPending}
                      >
                        Mark Resolved
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {resolvedBounces.length > 0 && (
            <div className="mt-8">
              <h3 className="text-sm font-medium mb-4">Recently Resolved ({resolvedBounces.length})</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Resolved</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resolvedBounces.slice(0, 5).map((bounce) => (
                    <TableRow key={bounce.id}>
                      <TableCell className="font-medium">{bounce.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{bounce.bounce_type}</Badge>
                      </TableCell>
                      <TableCell>
                        {bounce.resolved_at && formatDistanceToNow(new Date(bounce.resolved_at), { addSuffix: true })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
